import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';

const benefits = [
  {
    icon: 'ri-signal-tower-line',
    title: 'Frecuencia RFW exclusiva',
    description: 'Un identificador digital único que representa a tu emisora dentro de la plataforma.',
  },
  {
    icon: 'ri-global-line',
    title: 'Página pública profesional',
    description: 'Perfil completo con información, programación, fotos, videos y enlace de escucha.',
  },
  {
    icon: 'ri-message-3-line',
    title: 'Cabina interactiva para locutores',
    description: 'Chat en vivo con oyentes durante las transmisiones, mensajes, saludos y moderación.',
  },
  {
    icon: 'ri-group-line',
    title: 'Comunidad de oyentes',
    description: 'Seguidores, interacciones, likes y una audiencia real conectada a tu emisora.',
  },
  {
    icon: 'ri-calendar-event-line',
    title: 'Eventos y concursos',
    description: 'Organizá eventos en vivo y concursos para tu audiencia directamente desde la plataforma.',
  },
  {
    icon: 'ri-verified-badge-line',
    title: 'Perfil verificable',
    description: 'Insignia de verificación que respalda la autenticidad de tu emisora frente a los oyentes.',
  },
  {
    icon: 'ri-list-check-2',
    title: 'Directorio oficial de emisoras',
    description: 'Tu radio listada en el directorio público con búsqueda por categoría, género y ubicación.',
  },
  {
    icon: 'ri-qr-code-line',
    title: 'Código QR de tu emisora',
    description: 'QR personalizado que dirige directo a la página pública de tu radio para compartir en redes, flyers y promociones.',
  },
  {
    icon: 'ri-rocket-line',
    title: 'Acceso a futuras herramientas',
    description: 'Nuevas funcionalidades, integraciones y actualizaciones que expanden tu emisora.',
  },
];

function CountUpNumber({ target, suffix = '', duration = 2000 }: { target: number; suffix?: string; duration?: number }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          const startTime = performance.now();
          const step = (currentTime: number) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(eased * target));
            if (progress < 1) {
              requestAnimationFrame(step);
            }
          };
          requestAnimationFrame(step);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration]);

  return (
    <span ref={ref} className="tabular-nums">
      {count.toLocaleString('es-ES')}{suffix}
    </span>
  );
}

export default function RadioRulesPage() {
  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Header />

      {/* Hero */}
      <section className="relative pt-20 pb-14 md:pt-32 md:pb-20 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(https://readdy.ai/api/search-image?query=abstract%20geometric%20sound%20waves%20radiating%20from%20a%20central%20glowing%20golden%20point%2C%20dark%20charcoal%20background%20with%20warm%20amber%20and%20copper%20gradients%2C%20minimalist%20broadcast%20technology%20aesthetic%2C%20elegant%20flowing%20frequency%20lines%2C%20sophisticated%20digital%20radio%20theme%2C%20soft%20luminous%20particles%20floating%20upward%2C%20premium%20tech%20atmosphere%2C%20no%20text%20no%20logos&width=1600&height=700&seq=radio-rules-hero-2026&orientation=landscape)`,
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/50 to-background-50"></div>
        <div className="relative max-w-4xl mx-auto px-4 md:px-6 text-center w-full">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur-sm text-white/90 text-xs font-semibold mb-6 border border-white/20">
            <i className="ri-radio-line"></i>
            Etapa de Lanzamiento
          </div>
          <img
            src="https://readdy.ai/api/search-image?query=minimalist%20text%20logo%20mark%20on%20transparent%20dark%20background%2C%20elegant%20typography%2C%20warm%20golden%20tones%2C%20sophisticated%20radio%20brand%20identity%2C%20premium%20look%2C%20no%20background%20elements&width=320&height=80&seq=radio-rules-logo-mark&orientation=landscape"
            alt="RadioFrecuenciaWeb"
            className="h-14 md:h-20 mx-auto mb-5 object-contain"
          />
          <p className="text-white/85 text-lg md:text-xl font-light mb-8 max-w-2xl mx-auto leading-relaxed">
            La nueva frecuencia de la radio está en la web. Administración, perfil público, cabina, comunidad, directorio, eventos y contenido. Sin servidores de streaming.
          </p>

          {/* Frequency counter spotlight */}
          <div className="inline-flex flex-col items-center bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl px-8 py-6 md:px-12 md:py-8 mb-8">
            <p className="text-white/60 text-xs md:text-sm uppercase tracking-widest mb-2">Frecuencias RFW disponibles</p>
            <p className="text-5xl md:text-7xl font-black text-white tracking-tight">
              <CountUpNumber target={11300} />
            </p>
            <p className="text-white/50 text-xs md:text-sm mt-2">en esta etapa de lanzamiento</p>
          </div>

          <p className="text-white/60 text-xs max-w-md mx-auto">
            Las frecuencias son limitadas y se asignan por orden de aprobación.
          </p>
        </div>
      </section>

      {/* Approval Process */}
      <section className="py-14 md:py-20">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              Cada emisora debe ser aprobada
            </h2>
            <p className="text-foreground-500 text-sm max-w-xl mx-auto">
              No todas las solicitudes son aceptadas. Revisamos cada aplicación para asegurar una red de emisoras activas, comprometidas y con presencia real en la comunidad.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { step: '01', icon: 'ri-file-list-3-line', title: 'Solicitud', desc: 'Completá el formulario con los datos de tu emisora, URL de stream y documentación.' },
              { step: '02', icon: 'ri-search-eye-line', title: 'Revisión', desc: 'Nuestro equipo evalúa tu aplicación, verifica la calidad del stream y la propuesta de contenido.' },
              { step: '03', icon: 'ri-check-double-line', title: 'Aprobación', desc: 'Si cumplís con los requisitos, recibís tu frecuencia RFW y acceso completo a la plataforma.' },
            ].map((item) => (
              <div key={item.step} className="bg-background-50 border border-background-200/70 rounded-2xl p-6 text-center group hover:border-accent-300/60 transition-colors">
                <span className="inline-block text-[10px] font-black text-accent-400 tracking-widest mb-3">{item.step}</span>
                <div className="w-14 h-14 rounded-2xl bg-accent-100 flex items-center justify-center mx-auto mb-4">
                  <i className={`${item.icon} text-accent-600 text-2xl`}></i>
                </div>
                <h3 className="font-bold text-foreground-900 text-sm mb-2">{item.title}</h3>
                <p className="text-foreground-500 text-xs leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-10 md:py-14 bg-background-100/60">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <div className="w-12 h-12 rounded-2xl bg-accent-100 flex items-center justify-center mx-auto mb-5">
            <i className="ri-focus-2-line text-accent-600 text-xl"></i>
          </div>
          <p className="text-foreground-700 text-base md:text-lg leading-relaxed font-medium">
            Nuestro objetivo es construir una red de emisoras activas, comprometidas con sus oyentes
            y con una presencia real dentro de la comunidad.
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-14 md:py-20">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-[11px] font-bold mb-4">
              Al ser aprobada
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              Tu estación recibe
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {benefits.map((benefit, i) => (
              <div
                key={i}
                className="bg-background-50 border border-background-200/70 rounded-2xl p-5 hover:border-accent-300/60 transition-colors group"
              >
                <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center mb-4 group-hover:bg-accent-200/80 transition-colors">
                  <i className={`${benefit.icon} text-accent-600 text-lg`}></i>
                </div>
                <h3 className="font-bold text-foreground-900 text-sm mb-1.5">{benefit.title}</h3>
                <p className="text-foreground-500 text-xs leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Frequency Policy */}
      <section className="py-14 md:py-20 bg-background-100/60">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="bg-background-50 border border-background-200/70 rounded-2xl p-6 md:p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                <i className="ri-shield-keyhole-line text-amber-600 text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-foreground-900 text-sm">Reserva de frecuencia</h3>
                <p className="text-foreground-500 text-xs">Política de asignación</p>
              </div>
            </div>
            <p className="text-foreground-600 text-sm leading-relaxed">
              Una vez ocupada una frecuencia, queda reservada para esa emisora mientras mantenga
              su cuenta activa y cumpla con las políticas de la plataforma. Las frecuencias
              liberadas por inactividad o cancelación retornan al pool disponible para nuevas emisoras.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-14 md:py-20">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
            Solicita tu ingreso hoy
          </h2>
          <p className="text-foreground-500 text-sm mb-8 max-w-lg mx-auto">
            Forma parte de la nueva generación de radio digital. Las frecuencias son limitadas y se entregan por orden de aprobación.
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Link
              to="/register-radio"
              className="px-7 py-3 rounded-xl bg-accent-500 text-background-50 dark:text-foreground-950 font-semibold text-sm hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
            >
              <i className="ri-radio-line"></i>
              Solicitar frecuencia
            </Link>
            <Link
              to="/pricing"
              className="px-7 py-3 rounded-xl bg-background-50 border border-background-200 text-foreground-700 font-medium text-sm hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
            >
              <i className="ri-price-tag-3-line"></i>
              Ver planes
            </Link>
          </div>
          <p className="text-foreground-400 text-[11px] mt-4 max-w-lg mx-auto leading-relaxed">
            <strong>Importante:</strong> RadioFrecuenciaWeb <strong>NO</strong> vende streaming de audio, servidores SHOUTcast/Icecast, AutoDJ, ancho de banda ni licencias musicales. Cada radio debe contratar su propio proveedor externo de streaming y registrar su Stream URL en la plataforma.
          </p>
        </div>
      </section>

      {/* Bottom legal links */}
      <div className="pb-14">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="p-6 bg-background-100/80 rounded-2xl border border-background-200/70 text-center">
            <p className="text-foreground-500 text-xs mb-3">Documentos legales</p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <Link to="/terms" className="px-4 py-2 rounded-lg bg-background-50 border border-background-200 text-xs font-medium text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-file-text-line mr-1.5"></i>
                Términos y Condiciones
              </Link>
              <Link to="/privacy" className="px-4 py-2 rounded-lg bg-background-50 border border-background-200 text-xs font-medium text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-shield-check-line mr-1.5"></i>
                Política de Privacidad
              </Link>
              <Link to="/cookies" className="px-4 py-2 rounded-lg bg-background-50 border border-background-200 text-xs font-medium text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-file-settings-line mr-1.5"></i>
                Política de Cookies
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}